import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import ZodiacGlyph from './ZodiacGlyph';
import {
    SIGN_ORDER,
    assignRadialLevels,
    getHouseLabels,
    getOriginLongitude,
    getSignLabelAngles,
    longitudeToAngle,
    polarToPoint
} from './chartGeometry';
import PLANET_IMAGES, { PLANET_SCALE } from './planetImages';
import styles from './styles.module.scss';

const C = 150;
const R_OUTER = 146;
const R_ZODIAC_INNER = 116;
// The house band is the wide ring between the zodiac band and a small core, as in
// the design (Figma 2267:5809); the planets sit in it.
const R_HOUSE_INNER = 54;
const R_GLYPH = 131;
// The numbers sit mid-way in their own light/dark ring (92-116), not against the
// zodiac band, so there is a gap between a number and either ring edge.
const R_HOUSE_RING_INNER = 92;
const R_HOUSE_NUMBER = 104;
const R_INNER_CUSP = R_HOUSE_INNER;
// Base ring for planets and the rings they are nudged onto when crowded.
const LEVEL_RADII = [74, 62, 80, 52];
const GLYPH_SIZE = 15;
const PLANET_SIZE = 20;

/**
 * An annular sector between two radii, from `startAngle` to `endAngle` (screen
 * angles, counter-clockwise positive, as `longitudeToAngle` returns). Used for the
 * alternating light/dark sign and house wedges.
 */
const sector = (rInner, rOuter, startAngle, endAngle) => {
    const a = polarToPoint(C, C, rOuter, startAngle);
    const b = polarToPoint(C, C, rOuter, endAngle);
    const c = polarToPoint(C, C, rInner, endAngle);
    const d = polarToPoint(C, C, rInner, startAngle);
    const sweep = (endAngle - startAngle + 360) % 360;
    const large = sweep > 180 ? 1 : 0;
    return `M${a.x.toFixed(2)} ${a.y.toFixed(2)}A${rOuter} ${rOuter} 0 ${large} 0 ${b.x.toFixed(2)} ${b.y.toFixed(2)}L${c.x.toFixed(
        2
    )} ${c.y.toFixed(2)}A${rInner} ${rInner} 0 ${large} 1 ${d.x.toFixed(2)} ${d.y.toFixed(2)}Z`;
};

const line = (from, to) => `M${from.x.toFixed(2)} ${from.y.toFixed(2)}L${to.x.toFixed(2)} ${to.y.toFixed(2)}`;

/**
 * SVG wheel (Figma 1566:12981, Birth Chart tab). Ascendant at 9 o'clock, zodiac
 * counter-clockwise. Without houses (no birth time/place) the cusps, house
 * numbers and ascendant marker are dropped and Aries 0 takes 9 o'clock.
 *
 * Colours are the design's (sampled from Figma 2267:5809): a dark navy ring, signs
 * alternating between two near-black blues, houses alternating between two
 * slightly lighter ones. `tone='dark'` is the same wheel as scenery (the flow's
 * intro modal), which leaves out the Ascendant marker.
 */
const ChartWheel = ({ placements, tone = 'default', planetSize = PLANET_SIZE }) => {
    const { t } = useTranslation();
    const houses = Array.isArray(placements?.houses) && placements.houses.length ? placements.houses : null;
    const origin = getOriginLongitude(placements);
    const bodies = assignRadialLevels(placements?.bodies || []);

    const signDividers = SIGN_ORDER.map((name, index) =>
        line(
            polarToPoint(C, C, R_ZODIAC_INNER, longitudeToAngle(index * 30, origin)),
            polarToPoint(C, C, R_OUTER, longitudeToAngle(index * 30, origin))
        )
    ).join('');

    return (
        <svg
            className={classNames(styles.wheel, { [styles.wheelDark]: tone === 'dark' })}
            viewBox="-12 -12 324 324"
            role="img"
            aria-label={t('birthChart.view.chart.wheelLabel')}
        >
            <circle className={styles.wheelZodiac} cx={C} cy={C} r={R_OUTER} />
            {SIGN_ORDER.map((name, index) => (
                <path
                    key={name}
                    className={index % 2 === 0 ? styles.wheelSignA : styles.wheelSignB}
                    d={sector(
                        R_ZODIAC_INNER,
                        R_OUTER - 2,
                        longitudeToAngle(index * 30, origin),
                        longitudeToAngle((index + 1) * 30, origin)
                    )}
                />
            ))}
            <circle className={styles.wheelHouses} cx={C} cy={C} r={R_ZODIAC_INNER} />
            {houses &&
                [...houses]
                    .sort((a, b) => a.number - b.number)
                    .map((house, index, sorted) => {
                        const next = sorted[(index + 1) % sorted.length];
                        return (
                            <path
                                key={`wedge-${house.number}`}
                                className={index % 2 === 0 ? styles.wheelHouseA : styles.wheelHouseB}
                                d={sector(
                                    R_HOUSE_RING_INNER,
                                    R_ZODIAC_INNER,
                                    longitudeToAngle(house.cuspLongitude, origin),
                                    longitudeToAngle(next.cuspLongitude, origin)
                                )}
                            />
                        );
                    })}
            <circle className={styles.wheelCore} cx={C} cy={C} r={R_HOUSE_INNER} />
            <path className={styles.wheelLine} d={signDividers} />

            {getSignLabelAngles(origin).map(({ name, angle }) => {
                const p = polarToPoint(C, C, R_GLYPH, angle);
                return (
                    <ZodiacGlyph
                        key={name}
                        sign={name}
                        size={GLYPH_SIZE}
                        x={p.x - GLYPH_SIZE / 2}
                        y={p.y - GLYPH_SIZE / 2}
                        strokeWidth={1.8}
                        className={styles.wheelGlyph}
                        data-testid="wheel-glyph"
                    />
                );
            })}

            {houses && (
                <g data-testid="wheel-houses">
                    {houses.map((house) => {
                        const angle = longitudeToAngle(house.cuspLongitude, origin);
                        return (
                            <path
                                key={house.number}
                                className={house.number === 1 ? styles.wheelCuspAsc : styles.wheelCusp}
                                d={line(polarToPoint(C, C, R_INNER_CUSP, angle), polarToPoint(C, C, R_ZODIAC_INNER, angle))}
                            />
                        );
                    })}
                    {getHouseLabels(houses, origin).map(({ number, angle }) => {
                        const p = polarToPoint(C, C, R_HOUSE_NUMBER, angle);
                        return (
                            <text key={number} className={styles.wheelHouseNumber} x={p.x} y={p.y}>
                                {number}
                            </text>
                        );
                    })}
                    {/* Ascendant arrow: inside the second circle, tip touching it (x = C - R_ZODIAC_INNER = 30). */}
                    <path
                        className={styles.wheelAscMarker}
                        d={`M${C - R_ZODIAC_INNER} ${C}L${C - R_ZODIAC_INNER + 6.5} ${C - 3.5}L${C - R_ZODIAC_INNER + 6.5} ${C + 3.5}Z`}
                        data-testid="wheel-asc"
                    />
                </g>
            )}

            {bodies.map((body) => {
                const radius = LEVEL_RADII[Math.min(body.level, LEVEL_RADII.length - 1)];
                const p = polarToPoint(C, C, radius, longitudeToAngle(body.longitude, origin));
                const href = PLANET_IMAGES[body.key];
                if (!href) return null;
                const size = planetSize * (PLANET_SCALE[body.key] || 1);
                return (
                    <image
                        key={body.key}
                        href={href}
                        x={p.x - size / 2}
                        y={p.y - size / 2}
                        width={size}
                        height={size}
                        data-testid={`wheel-body-${body.key}`}
                    />
                );
            })}
        </svg>
    );
};

export default ChartWheel;
